import React, { useEffect, useState } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { LoadingBall } from '@depop/web-ui-kit/LoadingBall';
import { PrimaryTabs } from '@depop/web-ui-kit/PrimaryTabs';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { FeedbackModalRow } from './FeedbackModalRow';
import styles from './styles.module.css';

import { ErrorMessage } from '@/components/ErrorMessage';
import { InfiniteScroll } from '@/components/InfiniteScroll';
import { TabPanels, TabPanel } from '@/components/TabPanels';
import {
  RQ_FEEDBACK_BUYER,
  RQ_FEEDBACK_SELLER,
} from '@/modules/ReactQuery/cacheKeys';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { type PresentationFeedbackPageInfo } from '@/modules/feedback/aliases';
import { fetchFeedback } from '@/modules/feedback/api';
import { FeedbackRole, type FeedbackItem } from '@/modules/feedback/types';
import { useIsViewingOwnShop } from '@/modules/shop/hooks/useIsViewingOwnShop';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type TabConfiguration = {
  id: FeedbackRole;
  value: number;
  label: string;
  ariaLabel: string;
  hasError: boolean;
  data?: FeedbackItem[];
  isSuccess: boolean;
  page_info?: PresentationFeedbackPageInfo;
  fetchNextPage: () => void;
  isLoading: boolean;
  noResultsMessage: string;
};

type Props = {
  sellerId: number;
  sellerUsername: string;
};

export function FeedbackModal({ sellerId, sellerUsername }: Props) {
  const [activeTab, setActiveTab] = useState(0);
  const tShop = useTranslations('shop');
  const tCommon = useTranslations('common');
  const [, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const isViewingOwnShop = useIsViewingOwnShop();

  useEffect(() => {
    const section = activeTab === 0 ? 'sold' : 'purchased';
    sendActivityTrackerEvent(
      ActivityTrackerEventType.USER_PROFILE_REVIEWS_VIEW,
      { section }
    );
  }, [activeTab]);

  const {
    data: sellerFeedbackData,
    isError: fetchSellerFeedbackError,
    isSuccess: fetchSellerFeedbackSuccess,
    fetchNextPage: fetchNextSellerFeedbackPage,
    isLoading: fetchSellerFeedbackLoading,
    isFetchingNextPage: isFetchingNextSellerFeedbackPage,
  } = useInfiniteQuery({
    queryKey: [RQ_FEEDBACK_SELLER, sellerId],
    queryFn: ({ pageParam }) =>
      fetchFeedback({
        role: FeedbackRole.Seller,
        sellerId,
        offset: pageParam,
      }),
    enabled: activeTab === 0,
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.data.page_info?.last,
  });

  const {
    data: buyerFeedbackData,
    isError: fetchBuyerFeedbackError,
    isSuccess: fetchBuyerFeedbackSuccess,
    fetchNextPage: fetchNextBuyerFeedbackPage,
    isLoading: fetchBuyerFeedbackLoading,
    isFetchingNextPage: isFetchingNextBuyerFeedbackPage,
  } = useInfiniteQuery({
    queryKey: [RQ_FEEDBACK_BUYER, sellerId],
    queryFn: ({ pageParam }) =>
      fetchFeedback({
        role: FeedbackRole.Buyer,
        sellerId,
        offset: pageParam,
      }),
    enabled: activeTab === 1,
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.data.page_info?.last,
  });

  const sellerFeedback = sellerFeedbackData?.pages?.flatMap(
    (page) => page.data.objects
  );
  const buyerFeedback = buyerFeedbackData?.pages?.flatMap(
    (page) => page.data.objects
  );

  const sellerAriaLabel = tShop('SellerFeedbackReviews', {
    username: sellerUsername,
  });
  const buyerAriaLabel = tShop('BuyerFeedbackReviews', {
    username: sellerUsername,
  });

  const feedbackTabsDetails: TabConfiguration[] = [
    {
      id: FeedbackRole.Seller,
      value: 0,
      label: tCommon('SoldSingular'),
      ariaLabel: sellerAriaLabel,
      hasError: fetchSellerFeedbackError,
      data: sellerFeedback,
      isSuccess: fetchSellerFeedbackSuccess,
      page_info:
        sellerFeedbackData?.pages[sellerFeedbackData?.pages?.length - 1]?.data
          ?.page_info,
      fetchNextPage: fetchNextSellerFeedbackPage,
      isLoading: fetchSellerFeedbackLoading || isFetchingNextSellerFeedbackPage,
      noResultsMessage: isViewingOwnShop
        ? tShop('NoOwnSellingFeedback')
        : tShop('NoFeedback', { username: sellerUsername }),
    },
    {
      id: FeedbackRole.Buyer,
      value: 1,
      label: tShop('Purchased'),
      ariaLabel: buyerAriaLabel,
      hasError: fetchBuyerFeedbackError,
      data: buyerFeedback,
      isSuccess: fetchBuyerFeedbackSuccess,
      page_info:
        buyerFeedbackData?.pages[buyerFeedbackData?.pages?.length - 1]?.data
          ?.page_info,
      fetchNextPage: fetchNextBuyerFeedbackPage,
      isLoading: fetchBuyerFeedbackLoading || isFetchingNextBuyerFeedbackPage,
      noResultsMessage: isViewingOwnShop
        ? tShop('NoOwnBuyingFeedback')
        : tShop('NoFeedback', { username: sellerUsername }),
    },
  ];

  return (
    <>
      <PrimaryTabs
        tabs={feedbackTabsDetails}
        activeValue={activeTab}
        onChange={(e) => setActiveTab(Number(e.target.value))}
        name="feedback-tabs"
        aria-label={activeTab === 0 ? sellerAriaLabel : buyerAriaLabel}
      />
      <TabPanels activeIndex={activeTab}>
        {feedbackTabsDetails.map(
          ({
            id,
            ariaLabel,
            hasError,
            isSuccess,
            data,
            fetchNextPage,
            page_info,
            isLoading,
            noResultsMessage,
          }) => {
            const hasData = data && data.length > 0;
            const showItems = isLoading || hasData;
            return (
              <TabPanel
                key={id}
                id={id}
                aria-label={ariaLabel}
                className={styles.tabPanel}
              >
                {isSuccess && !data?.length && (
                  <div className={styles.message}>
                    <Body variant="sm-regular">{noResultsMessage}</Body>
                  </div>
                )}
                {hasError && (
                  <div className={styles.message}>
                    <ErrorMessage type="body">
                      {tCommon('500.Message')}
                    </ErrorMessage>
                  </div>
                )}
                {showItems && (
                  <div className={styles.items}>
                    <InfiniteScroll
                      onEndReached={fetchNextPage}
                      hasMore={page_info?.has_more ?? false}
                      LoadingComponent={<LoadingBall />}
                      loading={isLoading}
                    >
                      {data?.map((userRow) => (
                        <FeedbackModalRow
                          key={userRow.product.id}
                          {...userRow}
                        />
                      ))}
                    </InfiniteScroll>
                  </div>
                )}
              </TabPanel>
            );
          }
        )}
      </TabPanels>
    </>
  );
}
